import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import ConfirmDialog from 'components/ConfirmDialog';
import useStartOperator from 'lib/hooks/useStartOperator';
import { getMachine } from 'lib/selectors/getMachine';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';
import { getUnattendedOperator } from 'lib/selectors/getUnattendedOperator';
import { getClockOutModalOpen } from 'lib/selectors/getClockOutModalOpen';
import { getClockOutFlowOpen } from 'lib/selectors/getClockOutFlowOpen';
import { getShiftHandoverOpen } from 'lib/selectors/getShiftHandoverOpen';
import { throwSuccessToast, throwErrorToast } from 'lib/utils/toast';
import { now, toISO } from 'lib/utils/date';

/**
 * Offers to sign in the "Unattended" operator after an APM operator signs out.
 *
 * The manual labor-ticket flow asks this inside the embedded labor-ticket form,
 * which APM sign-out never shows. On APM, sign-out instead opens the ClockOut
 * modal (and, when applicable, the multi-ticket gauntlet / shift-handover
 * drawer). Once that whole sequence has closed, we ask whether to keep the
 * machine running lights-out under the Unattended operator.
 *
 * This component only observes Redux state — no changes to the sign-out or
 * clock-out flows are required.
 */
export const UnattendedOperatorPrompt = React.memo(() => {
  const { t } = useTranslation();
  const startOperator = useStartOperator();

  const isApmERPJobFlow = useSelector(getIsApmERPJobFlow);
  const unattendedOperator = useSelector(getUnattendedOperator);
  const machine = useSelector(getMachine);
  const currentOperator = useSelector(getCurrentOperator);
  const clockOutModalOpen = useSelector(getClockOutModalOpen);
  const clockOutFlowOpen = useSelector(getClockOutFlowOpen);
  const shiftHandoverOpen = useSelector(getShiftHandoverOpen);

  const [isOpen, setIsOpen] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);

  // Any part of the clock-out sequence being open means the operator is still
  // working through sign-out; only when all of it closes is the sequence "done".
  const clockOutSequenceActive =
    clockOutModalOpen || clockOutFlowOpen || shiftHandoverOpen;

  // Track the latest active value so the deferred check below reads it fresh.
  const activeRef = useRef(clockOutSequenceActive);
  activeRef.current = clockOutSequenceActive;

  // Armed once a clock-out sequence opens; fires once it fully closes.
  const armedRef = useRef(false);

  useEffect(() => {
    if (clockOutSequenceActive) {
      // Don't re-arm while our own prompt is showing (avoids re-prompting).
      if (!isOpen) {
        armedRef.current = true;
      }
      return undefined;
    }

    if (!armedRef.current) {
      return undefined;
    }

    // React 16 doesn't batch dispatches made after an `await`, so the ClockOut
    // modal closing and the gauntlet / shift-handover opening land in separate
    // renders — leaving a transient frame where every flag is false. Defer to
    // the next macrotask and re-read the latest value so we only fire at the
    // real end of the sequence, not in that gap.
    const timer = setTimeout(() => {
      if (activeRef.current) {
        return;
      }
      armedRef.current = false;
      if (isApmERPJobFlow && unattendedOperator && !currentOperator) {
        setIsOpen(true);
      }
    }, 0);

    return () => {
      clearTimeout(timer);
    };
  }, [
    clockOutSequenceActive,
    isOpen,
    isApmERPJobFlow,
    unattendedOperator,
    currentOperator,
  ]);

  const handleConfirm = useCallback(async () => {
    if (isSigningIn || !unattendedOperator || !machine?.id) {
      setIsOpen(false);
      return;
    }
    setIsSigningIn(true);
    try {
      // startOperator resolves (doesn't throw) on two non-success paths: it
      // returns null while another sign-in is in flight, and an Apollo `errors`
      // array comes back resolved. Treat both as failures — a false "signed in"
      // here would leave the machine running lights-out with no labor
      // attribution and nobody present to notice. Start the run at confirmation
      // time (when Yes is tapped), matching SelectOperator's call site.
      const result = await startOperator({
        machineId: machine.id,
        operatorId: unattendedOperator.id,
        startAt: toISO(now().valueOf()),
      });
      if (!result || result.errors) {
        throwErrorToast(t('Failed to sign in the Unattended operator'));
        return;
      }
      throwSuccessToast(t('Signed in the Unattended operator'));
    } catch (err) {
      throwErrorToast(t('Failed to sign in the Unattended operator'));
    } finally {
      setIsSigningIn(false);
      setIsOpen(false);
    }
  }, [isSigningIn, unattendedOperator, machine, startOperator, t]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
  }, []);

  if (!isOpen) {
    return null;
  }

  return (
    <ConfirmDialog
      title={t('Sign in the Unattended operator?')}
      confirmText={t('Yes')}
      cancelText={t('No')}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );
});

export default UnattendedOperatorPrompt;
